import { computed, ref } from "vue";
import { defineStore } from "pinia";
import UserService from "@/core/services/UserService";
import type { Team } from "@/types/team";
import type { User } from "@/types/user";
import UserStoreLS from "@/core/localStorage/UserLS";
import { ElMessage } from "element-plus";

const freeTierPlanKeys = new Set<string>(
  (import.meta.env.VITE_APP_FREE_TIER_PLAN_KEYS || "")
    .split(",")
    .map((key: string) => key.trim())
    .filter(Boolean)
)

// Special team type flags in the team config, that enable additional functionality in the webapp
type TeamFlagKey = "is_empyrean_team" | "is_serrano_team"

function hasTrueTeamFlag(teamInfo: Team | undefined, key: TeamFlagKey): boolean {
  const sources: Array<Record<string, unknown> | undefined> = [
    teamInfo as Record<string, unknown> | undefined,
    teamInfo?.config
  ]

  return sources.some(source =>
    !!source &&
    Object.prototype.hasOwnProperty.call(source, key) &&
    source[key] === true
  )
}

export const useUserStore = defineStore("user", () => {
  const user = ref<User | undefined>(UserStoreLS.getStoredUser());
  const team = ref<Team | undefined>(UserStoreLS.getStoredTeam());
  const team_members = ref<User[]>([]);

  const is_serrano_team = computed<boolean>(() =>
    hasTrueTeamFlag(team.value, "is_serrano_team")
  )
  const is_empyrean_team = computed<boolean>(() =>
    hasTrueTeamFlag(team.value, "is_empyrean_team")
  )
  const is_team_account = computed<boolean>(() => !!team.value?.name?.trim())
  const is_free_tier_team = computed<boolean>(() => {
    const planKey = team.value?.plan_key
    return !!planKey && freeTierPlanKeys.has(planKey)
  })

  /**
   * Loads the logged in user profile into the `user` field of userStore
   * @returns An error string if there was a problem. Nothing otherwise
   */
  async function load_user() {
    let _team: Team | undefined
    await Promise.all([
      UserService.get_me().then(({data}) => { user.value = data as User }),
      UserService.get_my_team().then(({data}) => { _team = data })
    ])
      .then(() => {
        if(!user.value || !_team) { return }
        team.value = _team
        UserStoreLS.storeUser(user.value)
        UserStoreLS.storeTeam(team.value)
      })
      .catch(({response}) => {
        UserStoreLS.deleteUser()
        console.error("Error loading user or team: ", response)
        ElMessage({
          type: "error",
          message: "Error loading user profile!"
        })
      })
  }

  /**
   * Loads all members of the logged in user's team into `team_members`.
   */
  async function load_team_members() {
    await UserService.list_company_users(true)
      .then(({data}) => { team_members.value = data })
      .catch(({response}) => {
        team_members.value = []
        console.error("Error loading team members: ", response)
        ElMessage({
          type: "error",
          message: "Error loading team members!"
        })
      })
  }

  /**
   * Deletes the cached User from localStorage and clears the in-memory profile.
   */
  function delete_cached_user() {
    user.value = undefined
    team.value = undefined
    team_members.value = []
    UserStoreLS.deleteUser()
  }


  return {
    user,
    team,
    team_members,
    is_serrano_team,
    is_empyrean_team,
    is_team_account,
    is_free_tier_team,
    load_user,
    load_team_members,
    delete_cached_user
  };
});
